import { z } from 'zod';

// #195 Weitergabe im Fachbereich — Verträge zwischen API und Oberfläche
// (docs/features/weitergabe-195/architektur.md, Abschnitt „Shared").
//
// Die API arbeitet auf Testfällen, nicht auf Einträgen: eine Zuweisung je Testfall im Plan
// (`test_plan_items.assigned_tester_id`). Sichtbare Zahlen zählen dagegen Einträge (Testfall ×
// Kette) — deshalb trägt `HandoverResult` beides (`moved` und `moved_entries`).

const TestCaseIdsSchema = z.array(z.string().uuid()).min(1).max(500);

/**
 * Abgeben (`give`, mit Ziel) oder Übernehmen (`take`, Ziel ist immer der Aufrufer). Ein Ziel bei
 * `take` gibt es bewusst nicht — es könnte nur falsch sein.
 */
export const HandoverRequestSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('give'),
    test_case_ids: TestCaseIdsSchema,
    to_user_id: z.string().uuid(),
  }),
  z.object({ mode: z.literal('take'), test_case_ids: TestCaseIdsSchema }),
]);
export type HandoverRequest = z.infer<typeof HandoverRequestSchema>;

/**
 * Zustand eines Eintrags aus Sicht der Weitergabe (aus der Playlist abgeleitet). `retest` = Retest
 * bereit (Defect behoben, Lauf-Ergebnis `error`/`blocked`); `repeat` = Lauf-Ergebnis „Wiederholen".
 */
export type HandoverEntryLabel = 'open' | 'retest' | 'repeat' | 'waiting' | 'blocked';

export interface HandoverEntryView {
  test_case_id: string;
  e2e_id: string | null;
  human_id: string;
  title_de: string;
  title_en: string;
  e2e_name_de: string | null;
  e2e_name_en: string | null;
  label: HandoverEntryLabel;
}

/** Warum ein eigener Testfall keine Weitergabe-Ziele hat (Tooltip am deaktivierten Kästchen). */
export type HandoverBlockReason = 'no_teilprojekt' | 'no_colleague';

export interface HandoverOverview {
  /** Nur meine weitergebbaren Testfälle; `reason` ist gesetzt, wenn `targets` leer ist. */
  mine: { test_case_id: string; targets: string[]; reason: HandoverBlockReason | null }[];
  /** Alle Kollegen über meine Teilprojekte, je mit den Einträgen, die ich übernehmen darf. */
  colleagues: { user_id: string; name: string; entries: HandoverEntryView[] }[];
}

/** Warum ein Testfall bei der Weitergabe übersprungen wurde (Teilerfolg statt Alles-oder-nichts). */
export type HandoverSkipReason =
  | 'not_in_plan'
  | 'not_owner'
  | 'not_movable'
  | 'not_colleague'
  | 'changed';

export interface HandoverResult {
  moved: string[];
  moved_entries: number;
  skipped: { test_case_id: string; reason: HandoverSkipReason }[];
}

/** Eine wirksame letzte Weitergabe je Testfall (Plan-Detail: „umverteilt von …"). */
export interface HandoverLogEntry {
  test_case_id: string;
  mode: 'give' | 'take';
  from_user_id: string;
  from_name: string;
  to_user_id: string;
  actor_user_id: string | null;
  at: string;
}
